import React from 'react';
import './CoachMessageContent.css';

const INLINE_BULLET = /[ \t]+-\s+(?=\*\*[^*\n]{1,80}:\*\*)/g;
const LIST_ITEM = /^[-*]\s+(.+)$/;
const NUMBERED_ITEM = /^\d+[.)]\s+(.+)$/;
const HEADING = /^(#{1,6})\s+(.+?)\s*#*$/;

export const parseCoachMessageBlocks = (value) => {
  const text = typeof value === 'string' ? value.trim() : '';
  if (!text) return [];

  const lines = text
    .replace(/\r\n?/g, '\n')
    .replace(INLINE_BULLET, '\n- ')
    .split('\n')
    .map(line => line.trim());

  const blocks = [];
  let listItems = [];
  let listType = 'list';
  let listStart = 1;
  const flushList = () => {
    if (listItems.length > 0) {
      blocks.push(listType === 'ordered-list'
        ? { type: listType, start: listStart, items: listItems }
        : { type: listType, items: listItems });
      listItems = [];
    }
  };

  lines.forEach(line => {
    if (!line) {
      flushList();
      return;
    }
    const listMatch = line.match(LIST_ITEM);
    const numberedMatch = line.match(NUMBERED_ITEM);
    if (listMatch || numberedMatch) {
      const nextType = numberedMatch ? 'ordered-list' : 'list';
      const ordinal = numberedMatch ? Number.parseInt(line, 10) : 1;
      if (listType !== nextType || (numberedMatch && listItems.length && ordinal !== listStart + listItems.length)) flushList();
      listType = nextType;
      if (!listItems.length) listStart = ordinal;
      listItems.push((listMatch || numberedMatch)[1].trim());
      return;
    }
    flushList();
    const heading = line.match(HEADING);
    blocks.push(heading
      ? { type: 'heading', level: Math.max(3, Math.min(6, heading[1].length)), text: heading[2] }
      : { type: 'paragraph', text: line });
  });
  flushList();

  return blocks;
};

// Deliberately small Markdown subset: never parse raw HTML or generate links/images.
// React escapes all text nodes, including untrusted model output.
const InlineCoachText = ({ text }) => <>{String(text || '').split(/(\*\*[^*\n]+\*\*|(?<!\*)\*(?!\s)[^*\n]*?\S\*(?!\*))/g).filter(Boolean).map((part, index) => {
  if (part.startsWith('**') && part.endsWith('**')) return <strong key={index}>{part.slice(2, -2)}</strong>;
  if (part.startsWith('*') && part.endsWith('*')) return <em key={index}>{part.slice(1, -1)}</em>;
  return <React.Fragment key={index}>{part}</React.Fragment>;
})}</>;

const CoachMessageContent = ({ text, className = '' }) => {
  const blocks = parseCoachMessageBlocks(text);
  if (blocks.length === 0) return null;

  return (
    <div className={`coach-message-content${className ? ` ${className}` : ''}`}>
      {blocks.map((block, blockIndex) => block.type === 'list' || block.type === 'ordered-list' ? (
        React.createElement(block.type === 'list' ? 'ul' : 'ol', { key: `list-${blockIndex}`, ...(block.type === 'ordered-list' ? { start: block.start } : {}) },
          block.items.map((item, itemIndex) => (
            <li key={`${blockIndex}-${itemIndex}-${item}`}><InlineCoachText text={item} /></li>
          )))
      ) : block.type === 'heading' ? (
        React.createElement(`h${block.level}`, { key: `heading-${blockIndex}` }, <InlineCoachText text={block.text} />)
      ) : (
        <p key={`paragraph-${blockIndex}`}><InlineCoachText text={block.text} /></p>
      ))}
    </div>
  );
};

export default CoachMessageContent;
